import {
  type FieldPath,
  type FieldValues,
  type PathValue,
  type UseFormSetValue,
  type UseFormGetValues,
  type UseFormGetFieldState,
  type FormState,
  type Control,
} from 'react-hook-form'

import type { IGerenciadorPosConclusao } from '@/features/simulador/brasil/types'
import { CalculadoraGrupoParcelasMensais } from '@/features/simulador/shared/domain/calculadora-grupo-parcelas-mensais'
import type { TSimulacaoGrupoParcelaMensais } from '@/features/simulador/shared/domain/schemas/simulacao-grupo-parcela.schema'
import { showToast } from '@/infra/sonner'
import type { TGrupoMensais } from '@/utils/calculadora-valores-totais/processador-valores-decimal'

interface UseGrupoPosConclusaoParams<
  TPosConclusaoPath extends string,
  TSimulacaoPath extends string,
  TFieldValues extends FieldValues = FieldValues,
> {
  posConclusaoPath: TPosConclusaoPath
  simulacaoPath: TSimulacaoPath
  gerenciadorPosConclusao: IGerenciadorPosConclusao<TGrupoMensais>
  isPagamentoAVista: boolean
  dataSaldo: string | null | undefined
  dataSimulacao: string
  dataPrevisaoDeEntregaDoBloco: string
  setValue: UseFormSetValue<TFieldValues>
  getValues: UseFormGetValues<TFieldValues>
  getFieldState: UseFormGetFieldState<TFieldValues>
  formState: FormState<TFieldValues>
  control: Control<TFieldValues>
  mensais: TSimulacaoGrupoParcelaMensais | null
  posConclusao: TGrupoMensais | null
}

interface UseGrupoPosConclusaoReturn {
  invalid: boolean
  agrupadorParcelas: Array<{ quantidade: number; valor: number }>
  onChangeDataInicio: (dataInicio: string | null) => void
  onChangeDataFim: (dataFim: string | null) => void
  onDelete: () => void
  criarGrupoPosConclusao: () => void
}

export function useGrupoPosConclusao<
  TPosConclusaoPath extends string,
  TSimulacaoPath extends string,
  TFieldValues extends FieldValues = FieldValues,
>({
  posConclusaoPath,
  gerenciadorPosConclusao,
  isPagamentoAVista,
  dataSaldo,
  dataSimulacao,
  dataPrevisaoDeEntregaDoBloco,
  setValue,
  getValues,
  getFieldState,
  formState,
  mensais,
  posConclusao,
}: UseGrupoPosConclusaoParams<TPosConclusaoPath, TSimulacaoPath, TFieldValues>): UseGrupoPosConclusaoReturn {
  const { invalid } = getFieldState(posConclusaoPath as unknown as FieldPath<TFieldValues>, formState)

  const quantidade = posConclusao?.quantidade
  const valorPosConclusao = posConclusao?.valor

  const maiorValorMensal = CalculadoraGrupoParcelasMensais.maiorValor(mensais ?? [])

  const onChangeDataInicio = (dataInicio: string | null) => {
    const grupo = getValues(posConclusaoPath as unknown as FieldPath<TFieldValues>)
    if (!dataInicio || !grupo) return

    const ajustes = gerenciadorPosConclusao.calcularAjustePorDataInicio(grupo, dataInicio)
    setValue(
      `${posConclusaoPath}.quantidade` as unknown as FieldPath<TFieldValues>,
      ajustes.quantidade as unknown as PathValue<TFieldValues, FieldPath<TFieldValues>>,
    )
    setValue(
      `${posConclusaoPath}.dataInicio` as unknown as FieldPath<TFieldValues>,
      ajustes.dataInicio as unknown as PathValue<TFieldValues, FieldPath<TFieldValues>>,
    )
    setValue(
      `${posConclusaoPath}.dataFim` as unknown as FieldPath<TFieldValues>,
      ajustes.dataFim as unknown as PathValue<TFieldValues, FieldPath<TFieldValues>>,
    )
  }

  const onChangeDataFim = (dataFim: string | null) => {
    const grupo = getValues(posConclusaoPath as unknown as FieldPath<TFieldValues>)
    if (!dataFim || !grupo) return

    const ajustes = gerenciadorPosConclusao.calcularAjustePorDataFim(grupo, dataFim)
    setValue(
      `${posConclusaoPath}.quantidade` as unknown as FieldPath<TFieldValues>,
      ajustes.quantidade as unknown as PathValue<TFieldValues, FieldPath<TFieldValues>>,
    )
    setValue(
      `${posConclusaoPath}.dataInicio` as unknown as FieldPath<TFieldValues>,
      ajustes.dataInicio as unknown as PathValue<TFieldValues, FieldPath<TFieldValues>>,
    )
    setValue(
      `${posConclusaoPath}.dataFim` as unknown as FieldPath<TFieldValues>,
      ajustes.dataFim as unknown as PathValue<TFieldValues, FieldPath<TFieldValues>>,
    )
  }

  const onDelete = () =>
    setValue(
      posConclusaoPath as unknown as FieldPath<TFieldValues>,
      undefined as unknown as PathValue<TFieldValues, FieldPath<TFieldValues>>,
    )

  const criarGrupoPosConclusao = () => {
    try {
      if (isPagamentoAVista) {
        throw new Error(
          'Para esta simulação não é possível informar mensais pós-conclusão, uma vez que o pagamento é a vista.',
        )
      }
      if (!dataSaldo) throw new Error('Data do saldo é obrigatória para criar o grupo de pós-conclusão.')

      const validacaoPosConclusao = gerenciadorPosConclusao.deveGerarPosConclusao(
        dataPrevisaoDeEntregaDoBloco,
        dataSimulacao,
      )

      if (!validacaoPosConclusao.sucesso) {
        throw new Error(validacaoPosConclusao.mensagem)
      }

      const grupoPosConclusao = gerenciadorPosConclusao.criarGrupo(
        maiorValorMensal,
        dataSaldo,
        dataPrevisaoDeEntregaDoBloco,
        dataSimulacao,
      )
      setValue(
        posConclusaoPath as unknown as FieldPath<TFieldValues>,
        grupoPosConclusao as unknown as PathValue<TFieldValues, FieldPath<TFieldValues>>,
      )
    } catch (error) {
      if (error instanceof Error) {
        showToast({ message: error.message, variant: 'error', closeButton: true })
      }
    }
  }

  const agrupadorParcelas = quantidade ? [{ quantidade, valor: valorPosConclusao ?? 0 }] : []

  return {
    invalid,
    agrupadorParcelas,
    onChangeDataInicio,
    onChangeDataFim,
    onDelete,
    criarGrupoPosConclusao,
  }
}
